import apiClient from '@/api/client'
import type { Task, TaskStats, PaginatedResponse } from '@/types'

export interface TaskQueryParams {
  page?: number
  size?: number
  status?: string
  priority?: string
  assignedTo?: string   // converts to assigned_to via deepToSnakeCase interceptor
  entityType?: string
  entityId?: string
  /** Narrow the list to one workspace. Omitted means All Workspaces — no
   *  workspace predicate at all. This narrows WHERE the work happened; it
   *  never widens WHOSE work you see, which stays governed by visibility. */
  workspaceId?: string
  search?: string
  dueBefore?: string
  dueAfter?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface CreateTaskData {
  title: string
  description?: string
  status?: string
  priority?: string
  assigneeId?: string
  dueDate?: string
  reminderMinutesBefore?: number
  entityType?: string
  entityId?: string
  parentTaskId?: string
  checklist?: Array<{ text: string; completed?: boolean }>
  tags?: string[]
  estimatedMinutes?: number
}

export interface UpdateTaskData extends Partial<CreateTaskData> {}

export const tasksApi = {
  getTasks: (params?: TaskQueryParams) =>
    apiClient.get<PaginatedResponse<Task>>('/tasks', { params }),

  getMyTasks: (params?: { page?: number; size?: number; status?: string; workspaceId?: string }) =>
    apiClient.get<PaginatedResponse<Task>>('/tasks/my', { params }),

  getUpcomingTasks: (params?: { days?: number }) =>
    apiClient.get<Task[]>('/tasks/upcoming', { params }),

  getOverdueTasks: () =>
    apiClient.get<Task[]>('/tasks/overdue'),

  getTask: (id: string) =>
    apiClient.get<Task>(`/tasks/${id}`),

  createTask: (data: CreateTaskData) =>
    apiClient.post<Task>('/tasks', data),

  updateTask: (id: string, data: UpdateTaskData) =>
    apiClient.put<Task>(`/tasks/${id}`, data),

  completeTask: (id: string) =>
    apiClient.post<Task>(`/tasks/${id}/complete`),

  deleteTask: (id: string) =>
    apiClient.delete(`/tasks/${id}`),

  /** Stats take the same workspace filter as the list. If they did not, the
   *  cards above a filtered list would keep showing tenant-wide totals and the
   *  page would contradict itself. */
  getTaskStats: (params?: { workspaceId?: string }) =>
    apiClient.get<TaskStats>('/tasks/stats', { params }),
}
